import { AUFTRAG_API, BILLING_API, DOCUMENTS_API, STAMMDATEN_API } from '@Constants/backendConstants';
import {
    exportCounterTypesItems,
    getExportCounterName,
    getInvoiceCounterName,
    getOrderCounterName,
    getStammdatenCounterName,
    invoiceCounterTypesItems,
    orderCounterTypesItems,
    stammdatenCounterTypesItems,
} from '@Constants/counters';
import {
    EnumBillingCounter,
    EnumDynamicExportRunCounterTypes,
    EnumOrdersCounterTypes,
    EnumStammdatenCounterTypes,
} from '@Model';
import { Permission } from '@Constants/Permission.g';
import { FrameEditCounter, FrameListCounter } from '@Modules/Counters';
import {
    FrameCustomizeData,
    FrameEditDynamicExportDefinition,
    FrameListDynamicExportDefinitions,
    FrameListExportRuns,
    FramePrintOptions,
} from '@Modules/Documents';
import {
    FrameEditWorkflowDefinition,
    FrameEditWorkflowRun,
    FrameListWorkflowDefinition,
    FrameListWorkflowRun,
    FrameWorkflowMonitor,
} from '@Modules/AgenticOrchestrator';
import {
    FrameEditActionEventLog,
    FrameEditCommandAuditLog,
    FrameListActionEventLog,
    FrameListCommandAuditLog,
} from '@Modules/Auditing';
import { RulesetEditFrame, RulesetList } from '@Components/Rulesets';
import { Navigate, type RouteObject } from 'react-router';
import { setupModuleRouting } from './setupModuleRouting';
import { setupMailingSettingsRouting } from './setupMailingSettingsRouting';

type DynamicRouteObject = RouteObject & {
    disabled?: boolean;
};

function setupCountersRouting(): RouteObject {
    return {
        path: 'counters',
        children: [
            setupModuleRouting({
                resourcePath: 'orders',
                editFrame: (
                    <FrameEditCounter<EnumOrdersCounterTypes>
                        apiResourcePath={`${AUFTRAG_API}/counters`}
                        counterTypesItems={orderCounterTypesItems}
                        editPermission={Permission.AuftragsbearbeitungCountersUpdate}
                        deletePermission={Permission.AuftragsbearbeitungCountersDelete}
                    />
                ),
                listFrame: (
                    <FrameListCounter<EnumOrdersCounterTypes>
                        apiResourcePath={`${AUFTRAG_API}/counters`}
                        displayCounterType={getOrderCounterName}
                        counterTypeFilterOptions={orderCounterTypesItems}
                        moduleName={'OrderCounter'}
                        createPermission={Permission.AuftragsbearbeitungCountersCreate}
                    />
                ),
            }),
            setupModuleRouting({
                resourcePath: 'billing',
                editFrame: (
                    <FrameEditCounter<EnumBillingCounter>
                        apiResourcePath={`${BILLING_API}/counters`}
                        counterTypesItems={invoiceCounterTypesItems}
                        editPermission={Permission.BillingCountersUpdate}
                        deletePermission={Permission.BillingCountersDelete}
                    />
                ),
                listFrame: (
                    <FrameListCounter<EnumBillingCounter>
                        apiResourcePath={`${BILLING_API}/counters`}
                        displayCounterType={getInvoiceCounterName}
                        counterTypeFilterOptions={invoiceCounterTypesItems}
                        moduleName={'BillingCounter'}
                        createPermission={Permission.BillingCountersCreate}
                    />
                ),
            }),
            setupModuleRouting({
                resourcePath: 'stammdaten',
                editFrame: (
                    <FrameEditCounter<EnumStammdatenCounterTypes>
                        apiResourcePath={`${STAMMDATEN_API}/counters`}
                        counterTypesItems={stammdatenCounterTypesItems}
                        editPermission={Permission.StammdatenCountersUpdate}
                        deletePermission={Permission.StammdatenCountersDelete}
                    />
                ),
                listFrame: (
                    <FrameListCounter<EnumStammdatenCounterTypes>
                        apiResourcePath={`${STAMMDATEN_API}/counters`}
                        displayCounterType={getStammdatenCounterName}
                        counterTypeFilterOptions={stammdatenCounterTypesItems}
                        moduleName={'StammdatenCounter'}
                        createPermission={Permission.StammdatenCountersCreate}
                    />
                ),
            }),
            setupModuleRouting({
                resourcePath: 'exports',
                editFrame: (
                    <FrameEditCounter<EnumDynamicExportRunCounterTypes>
                        apiResourcePath={`${DOCUMENTS_API}/counters`}
                        counterTypesItems={exportCounterTypesItems}
                        editPermission={Permission.DocumentsCountersUpdate}
                        deletePermission={Permission.DocumentsCountersDelete}
                    />
                ),
                listFrame: (
                    <FrameListCounter<EnumDynamicExportRunCounterTypes>
                        apiResourcePath={`${DOCUMENTS_API}/counters`}
                        displayCounterType={getExportCounterName}
                        counterTypeFilterOptions={exportCounterTypesItems}
                        moduleName={'ExportCounter'}
                        createPermission={Permission.DocumentsCountersCreate}
                    />
                ),
            }),
        ],
    };
}

// @ai-generated 2026-09-10T00:00:00Z by niklas.eicker@tngtech.com using Claude Opus 5 (1M context)
function setupWorkflowDefinitionsRouting(): RouteObject {
    const moduleRoutes = setupModuleRouting({
        resourcePath: 'workflow_definitions',
        editFrame: <FrameEditWorkflowDefinition />,
        listFrame: <FrameListWorkflowDefinition />,
    });

    return {
        path: 'workflow_definitions',
        children: [...(moduleRoutes.children ?? []), { path: ':itemId/preview', element: <FrameWorkflowMonitor /> }],
    };
}

export function setupSettingsRouting(): RouteObject {
    const routes: DynamicRouteObject[] = [
        {
            path: '',
            element: <Navigate to="counters/orders" replace />,
        },
        setupModuleRouting({
            resourcePath: 'documents',
            editFrame: <FrameCustomizeData />,
            listFrame: <FramePrintOptions />,
        }),
        setupModuleRouting({
            resourcePath: 'exports',
            editFrame: <FrameEditDynamicExportDefinition />,
            listFrame: <FrameListDynamicExportDefinitions />,
        }),
        {
            path: 'export_runs',
            element: <FrameListExportRuns />,
        },
        setupModuleRouting({
            resourcePath: 'rulesets/orders',
            editFrame: (
                <RulesetEditFrame
                    apiPath={`${AUFTRAG_API}/rulesets`}
                    resourceCacheKey="OrderRuleset"
                    createPermission={Permission.AuftragsbearbeitungRulesetsCreate}
                    updatePermission={Permission.AuftragsbearbeitungRulesetsUpdate}
                    deletePermission={Permission.AuftragsbearbeitungRulesetsDelete}
                />
            ),
            listFrame: (
                <RulesetList
                    apiPath={`${AUFTRAG_API}/rulesets`}
                    name="OrderRulesetsList"
                    createPermission={Permission.AuftragsbearbeitungRulesetsCreate}
                />
            ),
        }),
        setupModuleRouting({
            resourcePath: 'rulesets/stammdaten',
            editFrame: (
                <RulesetEditFrame
                    apiPath={`${STAMMDATEN_API}/rulesets`}
                    resourceCacheKey="StammdatenRuleset"
                    createPermission={Permission.StammdatenRulesetsCreate}
                    updatePermission={Permission.StammdatenRulesetsUpdate}
                    deletePermission={Permission.StammdatenRulesetsDelete}
                />
            ),
            listFrame: (
                <RulesetList
                    apiPath={`${STAMMDATEN_API}/rulesets`}
                    name="StammdatenRulesetsList"
                    createPermission={Permission.StammdatenRulesetsCreate}
                />
            ),
        }),
        setupWorkflowDefinitionsRouting(),
        setupModuleRouting({
            resourcePath: 'workflow_runs',
            editFrame: <FrameEditWorkflowRun />,
            listFrame: <FrameListWorkflowRun />,
        }),
        setupModuleRouting({
            resourcePath: 'action_event_logs',
            editFrame: <FrameEditActionEventLog />,
            listFrame: <FrameListActionEventLog />,
        }),
        setupModuleRouting({
            resourcePath: 'command_audit_logs',
            editFrame: <FrameEditCommandAuditLog />,
            listFrame: <FrameListCommandAuditLog />,
        }),
        setupCountersRouting(),
        setupMailingSettingsRouting(),
    ];

    return {
        path: 'settings',
        children: routes.filter((route) => !route.disabled),
    };
}
